"use client";

import * as React from "react";
import { format } from "date-fns";
import { type Matcher } from "react-day-picker";

import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import { Calendar, type CalendarProps } from "@src/lib/ui/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { selectTriggerClassName } from "@src/lib/ui/Select";
import CalendarOutlineIcon from "@src/lib/icons/18px/calendar-outline";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import ChevronUpIcon from "@src/lib/icons/12px/chevron-up";

export interface DatePickerProps {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  defaultMonth?: Date;
  disabled?: Matcher | Matcher[];
  /** Custom trigger element; falls back to the default trigger. */
  trigger?: React.ReactNode;
  /** Default trigger style: `field` (input-like, for forms) or `button`
   * (outline pill with a chevron, for standalone pickers). */
  triggerVariant?: "field" | "button";
  /** Dismiss the calendar once a day is picked. Opt-in, since the existing
   * always-open behaviour is load-bearing for inline fields that stay put; set
   * it where the popover would otherwise cover a form's submit button.
   * Ignored with `footer`, which closes on Apply/Cancel. */
  closeOnSelect?: boolean;
  /** Clear / Cancel / Apply footer. Picks go to a draft that only reaches
   * `onSelect` on Apply; Cancel and dismissing drop it; Clear empties it. */
  footer?: boolean;
  /** Calendar cell size; `default` (32px) unless a roomier `large` (40px) is asked for. */
  cellSize?: CalendarProps["cellSize"];
}

export function DatePicker({
  date,
  onSelect,
  placeholder = "Pick a date",
  className,
  defaultMonth,
  disabled,
  trigger,
  triggerVariant = "field",
  closeOnSelect = false,
  footer = false,
  cellSize = "default",
}: DatePickerProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Date | undefined>(date);

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(date);
    setOpen(next);
  };

  const label = date ? format(date, "MMM d, yyyy") : placeholder;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {trigger ??
          (triggerVariant === "button" ? (
            <Button
              type="button"
              variant="outline"
              size="md"
              className={className}
            >
              <CalendarOutlineIcon data-icon="inline-start" />
              {label}
              {open ? (
                <ChevronUpIcon
                  data-icon="inline-end"
                  className="size-2.5 text-icon-secondary"
                />
              ) : (
                <ChevronDownIcon
                  data-icon="inline-end"
                  className="size-2.5 text-icon-secondary"
                />
              )}
            </Button>
          ) : (
            // A field trigger, not an action — style like an input, not a Button.
            <button
              type="button"
              data-empty={!date}
              className={cn(
                selectTriggerClassName,
                "min-w-0 justify-start gap-2 px-2.5",
                !date && "text-text-tertiary",
                className,
              )}
            >
              <CalendarOutlineIcon className="size-4 shrink-0 text-icon-secondary" />
              <span className="truncate" title={date ? label : undefined}>
                {label}
              </span>
            </button>
          ))}
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-y-auto rounded-2xl border-border-primary bg-bg-elevated p-0 shadow-large">
        <Calendar
          mode="single"
          cellSize={cellSize}
          selected={footer ? draft : date}
          onSelect={(value) => {
            if (footer) {
              setDraft(value);
              return;
            }
            onSelect?.(value);
            if (closeOnSelect) setOpen(false);
          }}
          autoFocus
          defaultMonth={(footer ? draft : date) || defaultMonth || new Date()}
          disabled={disabled}
        />
        {footer && (
          <div className="flex items-center gap-2 border-t border-border-primary p-3">
            <Button
              type="button"
              variant="ghost"
              size="md"
              disabled={!draft}
              onClick={() => setDraft(undefined)}
            >
              Clear
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="md"
                onClick={() => {
                  if (draft?.getTime() !== date?.getTime()) onSelect?.(draft);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
